import React, { useEffect, useState } from 'react';
import Highcharts, { NoDataOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { GlobalToken, theme } from 'antd';

import { EPChartDonutTooltipContainer, EPChartDonutContainer } from './styled';

const { useToken } = theme;

interface TooltipProps {
  data: { name: string; value: number; color?: string }[];
  token: GlobalToken;
}

const Tooltip = ({ data, token }: TooltipProps) => (
  <EPChartDonutTooltipContainer theme={{ token }}>
    {data?.map(dataItem => (
      <span key={dataItem?.name}>
        <span className="value">{dataItem?.value}</span>
        <span>{dataItem?.name}</span>
      </span>
    ))}
  </EPChartDonutTooltipContainer>
);

const initOptions = (data, innerTitle, size, color, backgroundColor, animation: boolean, token: GlobalToken) => ({
  chart: {
    type: 'pie', // donut chart is a highcharts pie with special styling
    backgroundColor: backgroundColor ?? token.colorBgContainer,
    height: size ?? 90,
    width: size ?? 90,
    style: {
      fontFamily: 'Heebo',
    },
  },
  title: {
    text: innerTitle ? `<span class='inner-title' style='color: ${color}'>${innerTitle}</span>` : '',
    verticalAlign: 'middle',
    y: 22,
  },
  noData: '' as NoDataOptions,
  credits: {
    enabled: false,
  },
  tooltip: {
    formatter: () => ReactDOMServer.renderToString(<Tooltip data={data} token={token} />),
    useHTML: true,
    borderColor: undefined,
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: undefined,
    hideDelay: 100,
    outside: true,
    shadow: false,
    style: { width: 120 },
  },
  plotOptions: {
    pie: {
      animation,
      borderColor: backgroundColor ?? token.colorBgContainer,
      borderWidth: 0,
      dataLabels: {
        enabled: false,
      },
      size: 70,
      states: {
        hover: {
          enabled: false,
        },
        inactive: {
          opacity: 1,
        },
      },
    },
  },
});

interface EPChartDonutProps {
  data: { name: string; value: number; color?: string }[];
  innerTitle?: string | number;
  bottomTitle?: string;
  size?: string;
  backgroundColor?: string;
  animation?: boolean;
}

const EPChartDonut = ({ data, innerTitle, bottomTitle, size, backgroundColor, animation }: EPChartDonutProps) => {
  const [options, setOptions] = useState<unknown>();
  const dominantColor = data?.[0]?.color;
  const { token } = useToken();

  const createChartData = () => {
    return data.map(dataItem => ({
      name: dataItem?.name,
      y: dataItem?.value,
      color: dataItem?.color,
    }));
  };

  useEffect(() => {
    const chartData = createChartData();

    const newOptions = {
      ...initOptions(data, innerTitle, size, dominantColor, backgroundColor, animation, token),
      series: [
        {
          name: 'series',
          data: chartData,
          innerSize: '60%',
        },
      ],
    };

    setOptions(newOptions);
  }, [data]);

  return (
    <EPChartDonutContainer>
      <HighchartsReact highcharts={Highcharts} options={options} />
      <span className="bottom-title" style={{ color: dominantColor }} data-testid="donut-chart-title">
        {bottomTitle}
      </span>
    </EPChartDonutContainer>
  );
};

export default EPChartDonut;
